import React, { useState, useEffect } from "react";
import axios from "axios";
import { AuthGuard } from "../App";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserCircle,
  faEnvelope,
  faPhone,
  faCalendarAlt,
  faCheckCircle,
  faUserTag,
  faSpinner,
  faExclamationCircle,
} from "@fortawesome/free-solid-svg-icons"; // Added relevant icons

const Profile = () => {
  const [usersData, setUsersData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchUsersData = async () => {
      try {
        const userDataString = localStorage.getItem("user");
        const userData = JSON.parse(userDataString);
        const userId = userData.unique;
        const token = localStorage.getItem("token");

        if (!token) {
          setError("Authentication token not found. Please log in again.");
          setLoading(false);
          return;
        }

        const response = await axios.get(
          `https://backend.inyumba.co.ke/iNyumba/users/${userId}/edit`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        setUsersData(response.data.response);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching user data:", err);
        setError("Failed to load profile data. Please try again.");
        setLoading(false);
      }
    };

    fetchUsersData();
  }, []);

  // --- Loading State ---
  if (loading) {
    return (
      <AuthGuard>
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
          <div className="flex flex-col items-center text-gray-600">
            <FontAwesomeIcon icon={faSpinner} spin className="text-5xl mb-4 text-indigo-500" />
            <p className="text-xl font-medium">Loading profile...</p>
          </div>
        </div>
      </AuthGuard>
    );
  }

  // --- Error State ---
  if (error) {
    return (
      <AuthGuard>
        <div className="min-h-screen flex items-center justify-center bg-red-50 p-6">
          <div className="flex flex-col items-center text-red-700 p-8 rounded-lg bg-white shadow-lg border border-red-200">
            <FontAwesomeIcon icon={faExclamationCircle} className="text-5xl mb-4" />
            <h2 className="text-2xl font-bold mb-2">Error</h2>
            <p className="text-center">{error}</p>
            <p className="mt-4 text-sm text-gray-500">Please refresh the page or contact support.</p>
          </div>
        </div>
      </AuthGuard>
    );
  }

  // --- Profile Display ---
  const profileItems = [
    { label: "Full Name", value: usersData.name, icon: faUserCircle },
    { label: "Email Address", value: usersData.email, icon: faEnvelope },
    { label: "Phone Number", value: usersData.phone, icon: faPhone },
    { label: "Account Status", value: usersData.status, icon: faCheckCircle },
    { label: "Access Role", value: usersData.role, icon: faUserTag },
    { label: "Date Joined", value: new Date(usersData.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }), icon: faCalendarAlt },
  ];

  return (
    <AuthGuard>
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-100 p-6 sm:p-10 lg:p-16 flex justify-center">
        <div className="w-full max-w-2xl bg-white bg-opacity-95 backdrop-blur-md rounded-xl shadow-2xl p-8 sm:p-10 border border-gray-200 animate-fade-in-down">
          {/* Header */}
          <div className="text-center mb-8 sm:mb-10">
            <h1 className="text-4xl font-extrabold text-gray-900 mb-2 drop-shadow-sm">
              Your Profile
            </h1>
            <p className="text-lg text-gray-600">
              <span className="font-semibold text-indigo-600">@{usersData.name?.split(' ')[0] || 'User'}</span> — Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>

          {/* Profile Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {profileItems.map((item, index) => (
              <div key={index} className="flex items-center p-5 bg-gray-50 rounded-lg shadow-sm border border-gray-100 hover:bg-gray-100 transition duration-200 ease-in-out">
                <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-indigo-100 text-indigo-600 rounded-full mr-4 shadow-md">
                  <FontAwesomeIcon icon={item.icon} className="text-xl" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">{item.label}</p>
                  <p className="text-lg font-semibold text-gray-800 break-words">{item.value}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Call to action / Footer text */}
          <div className="text-center mt-10 text-gray-500 text-sm">
            <p>Need to update your details? Please contact support for assistance.</p>
          </div>
          <div className="text-center">
          <p className="mt-2 text-sm text-gray-600">
            Want to change your password?{" "}
            <a
              href="/user/changepassword"
              className="font-medium text-indigo-600 hover:text-indigo-500 transition-colors duration-200"
            >
              Change Password
            </a>
          </p>
        </div>
        </div>
      </div>
    </AuthGuard>
  );
};

export default Profile;